import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Users, Plus, House, UsersRound, CirclePlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, LayoutGroup } from "framer-motion";
import { useUnseenFeedCount } from "@/hooks/useUnseenFeedCount";
import { useCustomMetricsTrialWarning } from "@/hooks/useCustomMetricsTrialWarning";
import { useAppSession } from "@/providers/AppSessionProvider";
import { useProfile } from "@/hooks/useProfile";

import { createPortal } from "react-dom";

const MobileBottomNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Get current user session
  const { session } = useAppSession();
  const userId = session?.user?.id;

  // Get unseen feed count
  const { data: unseenCount = 0 } = useUnseenFeedCount(userId);

  // Get custom metrics trial warning status
  const { data: showCustomMetricsWarning = false } = useCustomMetricsTrialWarning(userId);

  // Get user profile for avatar
  const { data: profile } = useProfile(userId);
  const avatarUrl = profile?.avatar_url;

  const handleNavigation = (path: string) => {
    // Navigate immediately if not already on this path
    if (path !== location.pathname) {
      // Scroll to top instantly before navigation
      window.scrollTo(0, 0);
      navigate(path);
    }
  };

  // Logic moved below navItems definition
  // if (!isMobile) return null;

  const navItems = [
    { icon: Users, iconFilled: UsersRound, label: t('nav.social'), path: "/social" },
    { icon: Home, iconFilled: Home, label: t('nav.home'), path: "/" },
    { icon: Plus, iconFilled: CirclePlus, label: t('nav.track'), path: "/track" },
  ];

  // Only show on main tabs and settings
  const shouldShow = navItems.some(item => item.path === location.pathname) || location.pathname === "/settings";

  if (!isMobile || !shouldShow) return null;

  return createPortal(
    <LayoutGroup>
      <nav className="fixed bottom-0 left-0 right-0 z-50 select-none" style={{ transform: 'translateZ(0)' }}>
        {/* Background extension that goes below the viewport */}
        <div className="absolute left-0 right-0 h-[200px] -bottom-[200px] bg-background/80 backdrop-blur-lg" />

        {/* Container with rounded corners */}
        <div className="relative overflow-hidden rounded-t-2xl shadow-lg border-t border-x border-border/50 select-none" style={{ transform: 'translateZ(0)' }}>
          {/* Backdrop with blur effect */}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-lg" />

          {/* Content */}
          <div className="relative flex items-start justify-evenly px-4 pt-1 pb-8 w-full">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = isActive ? item.iconFilled : item.icon;
              const isTrackButton = item.path === "/track";
              const isSocialButton = item.path === "/social";
              const isHomeButton = item.path === "/";
              const showBadge = isSocialButton && unseenCount > 0;
              const showWarningBadge = isHomeButton && showCustomMetricsWarning;

              return (
                <button
                  key={item.path}
                  data-nav-button={item.path === "/" ? "home" : item.path === "/social" ? "social" : undefined}
                  onClick={() => handleNavigation(item.path)}
                  className={cn(
                    "flex flex-col items-center justify-center px-6 py-2 rounded-xl transition-all duration-200 flex-1 min-w-0 active:scale-95 relative gap-0.5",
                    isActive
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {/* Active indicator bar - always render container, conditionally show bar */}
                  <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ top: '-4px' }}>
                    {isActive && (
                      <motion.div
                        layoutId="activeNavIndicator"
                        className="w-8 h-1 bg-foreground rounded-full"
                        transition={{
                          type: "spring",
                          stiffness: 350,
                          damping: 30
                        }}
                      />
                    )}
                  </div>
                  <div className="h-6 flex items-center justify-center relative">
                    {isTrackButton ? (
                      <motion.div
                        animate={{
                          filter: [
                            "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))",
                            "drop-shadow(0 0 6px rgba(34, 197, 94, 0.7))",
                            "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))",
                          ],
                          color: [
                            "hsl(var(--muted-foreground))",
                            "rgb(34, 197, 94)",
                            "hsl(var(--muted-foreground))",
                          ],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        <Icon className={cn(
                          "flex-shrink-0 transition-transform w-7 h-7",
                          isActive && "scale-110"
                        )} />
                      </motion.div>
                    ) : isHomeButton && avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Profile" 
                        className={cn(
                          "w-8 h-8 rounded-full object-cover flex-shrink-0 transition-transform mt-3",
                          isActive && "scale-110 ring-2 ring-foreground"
                        )}
                      />
                    ) : (
                      <Icon className={cn(
                        "flex-shrink-0 transition-transform w-6 h-6",
                        isActive && "scale-110"
                      )} />
                    )}
                    {showBadge && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                        {unseenCount > 99 ? "99+" : unseenCount}
                      </div>
                    )}
                    {showWarningBadge && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        !
                      </div>
                    )}
                  </div>
                  {/* Hide label for Home when avatar is shown */}
                  {!(isHomeButton && avatarUrl) && (
                    <span className="text-[10px] font-light tracking-wide">{item.label}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </LayoutGroup>,
    document.body
  );
};

export default MobileBottomNav;